import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from '../../../../Flux/hooks.ts'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.js'
import type { SinkConfig, SinkId, SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { isLoadingState } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import type { State } from '../../../../Flux/Slice/index.js'
import { OTHER_SERIES_KEY } from './Selector/CollapseSeries.ts'
import { IdentityKey } from '../../../Component/Charts/IdentityKey.tsx'
import { useTransformer } from '../Transformer/UseTransformer.tsx'
import { SinkContextRow, TaxaBreadcrumb } from './SinkContextRow.tsx'

type LegendParams = {
  title?: string
  showTotals?: boolean
}

const EMPTY_PATH: string[] = []

export default function ColorLegendSink({
  sourceId,
  sinkId, // eslint-disable-line @typescript-eslint/no-unused-vars
  config
}: {
  sourceId: SourceId
  sinkId: SinkId
  config: SinkConfig<LegendParams>
}) {
  const dispatch = useDispatch()

  const source = useSelector((s: typeof State) => s.visualization.sources[sourceId])
  const snapshot = source?.snapshot ?? null
  const shared = source?.shared ?? {}
  const status = source?.status ?? 'init'

  const selectedPath = shared.selectedPath ?? EMPTY_PATH
  const gradeThreshold = shared.gradeThreshold ?? 0

  const traitGroup = shared?.natureKPIs?.traitGroup
  const noTraitMode = shared?.natureKPIs?.noTraitMode ?? 'include'

  const seriesRes =
    traitGroup && traitGroup !== ''
      ? useTransformer(sourceId, undefined, 'traitStackedBarSeries', {
          selectedPath,
          gradeThreshold,
          traitGroup,
          scalingMode: shared?.scalingMode ?? 'ABSOLUTE',
          noTraitMode
        })
      : useTransformer(sourceId, undefined, 'taxaStackedBarSeries', {
          selectedPath,
          gradeThreshold,
          scalingMode: shared?.scalingMode ?? 'ABSOLUTE'
        })


  const { taxaInView, totalsByTaxon } = useMemo(() => {
    if (seriesRes.status !== 'ready') return { taxaInView: [] as string[], totalsByTaxon: {} as Record<string, number> }

    const keys = seriesRes.value.stackKeys ?? []
    const data = seriesRes.value.data ?? []

    const totals: Record<string, number> = {}
    for (const key of keys) {
      for (const row of data) {
        const value = row[key]
        if (!value || typeof value === 'string') continue
        totals[key] = (totals[key] ?? 0) + value
      }
    }

    return { taxaInView: keys, totalsByTaxon: totals }
  }, [seriesRes])

  if (!snapshot) {
    return (
      <div className="text-sm opacity-70 p-3">
        Waiting for data… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}
      </div>
    )
  }

  if (seriesRes.status === 'idle' || seriesRes.status === 'loading') {
    return (
      <div className="text-sm opacity-70 p-3">Computing… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}</div>
    )
  }

  if (seriesRes.status === 'error') {
    return <div className="text-sm opacity-70 p-3">Error computing legend.</div>
  }

  if (taxaInView.length === 0) {
    return <div className="text-sm opacity-70 p-3">No legend entries for current selection.</div>
  }

  const title = config.params?.title
  const showTotals = config.params?.showTotals ?? false
  const unitLabel = String(shared.countMode ?? 'Reads')

  const handleClickTaxon = (taxon: string) => {
    const nextPath = [...selectedPath, taxon]
    dispatch(visualizationSlice.actions.setTaxaZoom({ sourceId, path: nextPath }))
  }

  return (
    <div className="flex flex-col h-full text-xs">
      <SinkContextRow>
        {title && <span className="opacity-70">{title}</span>}
        <TaxaBreadcrumb path={selectedPath} />
      </SinkContextRow>

      <ul className="flex-1 overflow-auto flex flex-col gap-1 px-2 py-2">
        {taxaInView.map((taxon) => {
          const isOther = taxon === OTHER_SERIES_KEY
          const total = totalsByTaxon[taxon]

          return (
            <li
              key={taxon}
              className="flex items-center gap-2 cursor-pointer hover:bg-base-200/60 rounded px-1 py-0.5"
              onClick={traitGroup === undefined && !isOther ? () => handleClickTaxon(taxon) : undefined}
            >
              <IdentityKey name={taxon} />
              <span className="truncate" title={taxon}>
                {taxon}
              </span>
              {showTotals && total != null && (
                <span className="ml-auto opacity-60 whitespace-nowrap" title={`${total} ${unitLabel}`}>
                  {total.toLocaleString()} {unitLabel}
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
